/**
 * @file DeliveryQuoteSheet.tsx
 * @description Панель «Расчёт доставки» (phase19.12 шаг 5, режимы — шаг 5.2).
 *
 * Две работы, одно окно и два режима (`mode`):
 *  - `pick-for-outcome` — расчёт из вкладки «Доставка»: выбранный тариф
 *    подставляет стоимость для клиента и уезжает снапшотом вместе с исходом;
 *  - `apply-to-shipment` — расчёт внутри «Отгрузок»: тариф и места
 *    сохраняются в отгрузку и СУММУ ЗАКАЗА НЕ МЕНЯЮТ (F26).
 *
 * Режим — проп, а не два компонента: форма, валидация и список тарифов у них
 * общие, и разъехались бы на первой же правке. Разный только подвал.
 */

import { useEffect, useMemo, useState } from "react";

import { ApplyQuoteDialog } from "./ApplyQuoteDialog";
import { useHasPermission } from "../../auth/hooks";
import { DeliveryQuoteForm } from "../carriers/DeliveryQuoteForm";
import { DeliveryQuoteResultPanel } from "../carriers/DeliveryQuoteResultPanel";
import { useDeliveryQuote } from "../carriers/useDeliveryQuote";
import { Button } from "../ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import type { ShipmentDto } from "../../lib/api-orders";
import type { DeliveryQuoteOption } from "../../lib/api-shop-carriers";
import {
  formFromOrderDelivery,
  formFromShipment,
  formatRub,
  type DeliveryQuoteFormValue,
  type DeliveryQuoteSource,
} from "../../lib/delivery-quote";
import { quoteFailureReason } from "../../lib/delivery-quote-meta";

/** Зачем открыт расчёт: выбрать тариф заказу или применить его к отгрузке. */
export type DeliveryQuoteMode = "apply-to-shipment" | "pick-for-outcome";

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  /** Slug магазина — для ссылки на настройки перевозчика из причин отказа. */
  shopSlug?: string;
  /** Куда и что везём — узкая часть ответа о доставке (см. `DeliveryQuoteSource`). */
  delivery: DeliveryQuoteSource | null;
  /** Отгрузки-черновики для выбора цели применения. */
  drafts: ShipmentDto[];
  /** Открыт из карточки отгрузки — расчёт применится в неё же. */
  shipment?: ShipmentDto | null;
  goodsTotal?: number;
  mode?: DeliveryQuoteMode;
  /** Режим выбора: тариф уходит в условия заказа (стоимость + снапшот). */
  onPick?: (option: DeliveryQuoteOption, value: DeliveryQuoteFormValue) => void;
  /**
   * Итог последнего расчёта кодом причины: `null` — тарифы получены, иначе код
   * из `QUOTE_REASON_META`. Блок «Доставка» объясняет им пустую стоимость —
   * менеджер закрывает шторку и всё равно видит, почему цены нет (ревью 5.2).
   */
  onQuoteReason?: (reason: string | null) => void;
  onApplied: () => void;
}

export function DeliveryQuoteSheet({
  open,
  onClose,
  orderId,
  shopSlug,
  delivery,
  drafts,
  shipment,
  goodsTotal,
  mode = "apply-to-shipment",
  onPick,
  onQuoteReason,
  onApplied,
}: Props) {
  const canOpenIntegrations = useHasPermission("PLATFORM_MANAGE_INTEGRATIONS");
  const [applyOpen, setApplyOpen] = useState(false);
  const picking = mode === "pick-for-outcome";

  const initial = useMemo(() => {
    const base = formFromOrderDelivery(delivery);
    return shipment ? formFromShipment(shipment, base) : base;
  }, [delivery, shipment]);

  const clientItem = delivery?.deliveryItem ?? null;
  const controller = useDeliveryQuote({
    carrier: "SDEK",
    initial,
    items: delivery?.items,
    goodsTotal,
    preferTariffCode: shipment?.tariffCode ?? clientItem?.tariffCode ?? null,
  });

  const { reset } = controller;
  // Панель монтируется один раз: при каждом открытии подставляем свежие данные.
  useEffect(() => {
    if (open) reset(initial);
  }, [open, initial, reset]);

  const { quote, error, running } = controller;
  // Итог расчёта отдаём наружу, пока шторка открыта: закрыв её, менеджер
  // обязан видеть в блоке, почему стоимость пуста (ревью 5.2, §4).
  useEffect(() => {
    if (running || !onQuoteReason) return;
    if (!quote && !error) return;
    onQuoteReason(quoteFailureReason(quote, error));
  }, [quote, error, running, onQuoteReason]);

  const selected = controller.selected;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="right" className="w-full max-w-none gap-0 md:max-w-2xl">
          <SheetHeader className="shrink-0">
            <SheetTitle>Расчёт доставки</SheetTitle>
            <SheetDescription>
              {picking
                ? "Выбранный тариф станет стоимостью доставки для клиента — её можно поправить вручную."
                : "Расчёт для этой отгрузки. Сумму заказа не меняет."}
            </SheetDescription>
          </SheetHeader>

          <div className="-mx-6 flex-1 overflow-y-auto px-6 py-4">
            <div className="flex flex-col gap-5">
              <DeliveryQuoteForm
                value={controller.value}
                onChange={controller.patch}
                items={delivery?.items}
                clientPickupPointCode={delivery?.deliveryAddress?.pickupPointCode ?? null}
                autoWeightG={controller.quote?.packageUsed?.weightG ?? null}
                running={controller.running}
                onRun={() => void controller.run()}
              />
              <DeliveryQuoteResultPanel
                controller={controller}
                carrierSettingsHref={
                  shopSlug ? `/shop/${shopSlug}/carriers/SDEK/settings` : null
                }
                integrationsHref={canOpenIntegrations ? "/platform/integrations" : null}
                clientTariffCode={clientItem?.tariffCode ?? null}
                clientPrice={clientItem?.priceGross ?? null}
              />
            </div>
          </div>

          <div className="sticky bottom-0 flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-outline-variant bg-surface-lowest/95 pt-3 backdrop-blur">
            <div className="text-sm">
              {selected ? (
                <>
                  Выбран {selected.tariffCode} ·{" "}
                  <span className="font-semibold">клиенту {formatRub(selected.price)}</span>
                </>
              ) : (
                <span className="text-on-surface-variant">Тариф не выбран</span>
              )}
            </div>
            <Button
              className="min-h-11"
              disabled={!selected}
              onClick={() => {
                if (!selected) return;
                if (picking) onPick?.(selected, controller.value);
                else setApplyOpen(true);
              }}
            >
              {picking ? "Выбрать этот тариф" : "Применить к отгрузке"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {!picking && applyOpen && selected ? (
        <ApplyQuoteDialog
          open
          onClose={() => setApplyOpen(false)}
          orderId={orderId}
          value={controller.value}
          option={selected}
          items={delivery?.items ?? []}
          drafts={drafts}
          defaultTargetId={shipment?.id ?? null}
          clientPrice={clientItem?.priceGross ?? null}
          autoWeightG={controller.quote?.packageUsed?.weightG ?? null}
          onApplied={() => {
            setApplyOpen(false);
            onClose();
            onApplied();
          }}
        />
      ) : null}
    </>
  );
}
